import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import Button from '../common/Button'
import Logo from '../common/Logo'
import { supabase } from '@/lib/supabase'

export default function Navigation() {

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [userEmail, setUserEmail] = useState('')
  const [showForTeamsDropdown, setShowForTeamsDropdown] = useState(false)

  useEffect(() => {
    let mounted = true
    const init = async () => {
      const { data: { session } } = await supabase.auth.getSession()
      if (mounted) setUserEmail(session?.user?.email || '')
    }
    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      setUserEmail(session?.user?.email || '')
    })
    init()
    return () => {
      mounted = false
      sub?.subscription?.unsubscribe?.()
    }
  }, [])

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen)
  }

  return (
    <nav className="sticky top-0 z-50 bg-white border-b border-border shadow-sm">
      <div className="max-w-6xl mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          {/* Logo/Brand */}
          <Link to="/" aria-label="Go to home">
            <Logo withText={true} size={36} />
          </Link>

          {/* Desktop Navigation & CTAs */}
          <div className="hidden lg:flex items-center gap-8">
            <Link
              to="/scrimmages/bulletin"
              className="text-brand-green hover:text-brand-green/80 font-semibold transition-colors"
            >
              Find Scrimmages
            </Link>
            <button
              onClick={() => document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' })}
              className="text-brand-charcoal hover:text-brand-green font-medium transition-colors"
            >
              How It Works
            </button>

            {/* For Teams Dropdown */}
            <div
              className="relative"
              onMouseEnter={() => setShowForTeamsDropdown(true)}
              onMouseLeave={() => setShowForTeamsDropdown(false)}
            >
              <button
                className="text-brand-charcoal hover:text-brand-green font-medium transition-colors flex items-center gap-1"
              >
                For Teams
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {showForTeamsDropdown && (
                <div className="absolute top-full left-0 mt-2 w-56 bg-white border border-border rounded-lg shadow-lg py-2 z-50">
                  <Link
                    to="/dashboard"
                    className="block px-4 py-2 text-brand-charcoal hover:bg-brand-cream hover:text-brand-green transition-colors"
                    onClick={() => setShowForTeamsDropdown(false)}
                  >
                    <div className="font-medium">Team Dashboard</div>
                    <div className="text-xs text-brand-slate">Manage your team</div>
                  </Link>
                  <Link
                    to="/scrimmages/manage"
                    className="block px-4 py-2 text-brand-charcoal hover:bg-brand-cream hover:text-brand-green transition-colors"
                    onClick={() => setShowForTeamsDropdown(false)}
                  >
                    <div className="font-medium">Scrimmage Manager</div>
                    <div className="text-xs text-brand-slate">Post and manage scrimmages</div>
                  </Link>
                  <div className="border-t border-border my-2"></div>
                  <Link
                    to="/pricing"
                    className="block px-4 py-2 text-brand-charcoal hover:bg-brand-cream hover:text-brand-green transition-colors"
                    onClick={() => setShowForTeamsDropdown(false)}
                  >
                    <div className="font-medium">Communication Tools</div>
                    <div className="text-xs text-brand-slate">Dispatches, wiki, portal</div>
                  </Link>
                  <Link
                    to="/pricing"
                    className="block px-4 py-2 text-brand-charcoal hover:bg-brand-cream hover:text-brand-green transition-colors"
                    onClick={() => setShowForTeamsDropdown(false)}
                  >
                    <div className="font-medium">Pricing</div>
                    <div className="text-xs text-brand-slate">Free & Pro plans</div>
                  </Link>
                </div>
              )}
            </div>

            <Link
              to="/pricing"
              className="text-brand-charcoal hover:text-brand-green font-medium transition-colors"
            >
              Pricing
            </Link>

            {/* Divider */}
            <div className="h-6 w-px bg-border"></div>

            {/* Desktop CTAs */}
            <div className="flex items-center gap-4">
              {userEmail ? (
                <>
                  <span className="text-sm text-brand-slate whitespace-nowrap">Signed in as <strong className="text-brand-charcoal">{userEmail}</strong></span>
                  <Link to="/dashboard">
                    <Button variant="secondary" className="px-4 py-2">Dashboard</Button>
                  </Link>
                  <Button
                    variant="primary"
                    className="px-4 py-2"
                    onClick={async () => { await supabase.auth.signOut(); window.location.href = '/' }}
                  >
                    Sign Out
                  </Button>
                </>
              ) : (
                <>
                  <Link to="/auth?tab=signin">
                    <Button variant="secondary" className="px-6 py-2">
                      Sign In
                    </Button>
                  </Link>
                  <Link to="/scrimmages/bulletin">
                    <Button variant="primary" className="px-6 py-2">
                      Find Scrimmages
                    </Button>
                  </Link>
                </>
              )}
            </div>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={toggleMobileMenu}
            className="lg:hidden p-2 text-brand-charcoal hover:text-brand-green transition-colors"
            aria-label="Toggle mobile menu"
          >
            {mobileMenuOpen ? (
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : (
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            )}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden mt-4 pt-4 border-t border-border space-y-4">
            <Link
              to="/scrimmages/bulletin"
              className="block text-brand-green font-semibold transition-colors"
              onClick={() => setMobileMenuOpen(false)}
            >
              Find Scrimmages
            </Link>
            <button
              onClick={() => { setMobileMenuOpen(false); document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' }) }}
              className="block text-brand-charcoal hover:text-brand-green font-medium transition-colors text-left"
            >
              How It Works
            </button>

            {/* For Teams Section */}
            <div className="border-t border-border pt-4">
              <div className="text-xs text-brand-slate uppercase tracking-wide mb-2">For Teams</div>
              <div className="space-y-2 pl-2">
                <Link
                  to="/dashboard"
                  className="block text-brand-charcoal hover:text-brand-green font-medium transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Team Dashboard
                </Link>
                <Link
                  to="/scrimmages/manage"
                  className="block text-brand-charcoal hover:text-brand-green font-medium transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Scrimmage Manager
                </Link>
                <Link
                  to="/pricing"
                  className="block text-brand-charcoal hover:text-brand-green font-medium transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Communication Tools
                </Link>
              </div>
            </div>

            <Link
              to="/pricing"
              className="block text-brand-charcoal hover:text-brand-green font-medium transition-colors"
              onClick={() => setMobileMenuOpen(false)}
            >
              Pricing
            </Link>
            <div className="pt-4 space-y-2">
              {userEmail ? (
                <>
                  <div className="text-sm text-brand-slate">Signed in as <strong className="text-brand-charcoal">{userEmail}</strong></div>
                  <Link to="/dashboard" className="block" onClick={() => setMobileMenuOpen(false)}>
                    <Button variant="secondary" className="w-full">Dashboard</Button>
                  </Link>
                  <Button
                    variant="primary"
                    className="w-full"
                    onClick={async () => { await supabase.auth.signOut(); window.location.href = '/' }}
                  >
                    Sign Out
                  </Button>
                </>
              ) : (
                <>
                  <Link to="/auth?tab=signin" className="block" onClick={() => setMobileMenuOpen(false)}>
                    <Button variant="secondary" className="w-full">
                      Sign In
                    </Button>
                  </Link>
                  <Link to="/scrimmages/bulletin" className="block" onClick={() => setMobileMenuOpen(false)}>
                    <Button variant="primary" className="w-full">
                      Find Scrimmages
                    </Button>
                  </Link>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  )
}

